import { Capacitor, registerPlugin } from "@capacitor/core";

export type MundelyNativeWidgetKind = "nextStop" | "expenses" | "expenseHistory" | "documents" | "checklist" | "reminder" | "calculator" | "converter";
export type MundelyWidgetButtonStyle = "square" | "sharp" | "soft" | "rounded";

export interface MundelyNativeWidgetAppearance {
  bgColor: string;
  bgAlpha: number;
  iconColor: string;
  titleColor: string;
  textColor: string;
  accentColor: string;
  textSize: number;
  buttonStyle: MundelyWidgetButtonStyle;
  showHeader: boolean;
  showTripName: boolean;
  showSecondary: boolean;
  autoUpdate: boolean;
  maxChars?: number;
  checklistItems?: number;
  buttonColor?: string;
  secondaryButtonColor?: string;
  showTitle?: boolean;
  titleText?: string;
  showTransaction?: boolean;
  maxStops?: number;
  includePastStops?: boolean;
  documentDisplayMode?: "image" | "link";
}

export type MundelyNativeWidgetSettings = Record<MundelyNativeWidgetKind, MundelyNativeWidgetAppearance>;

export const MUNDELY_NATIVE_WIDGET_SETTINGS_KEY = "mundely.nativeWidgetSettings.v1";

export const MUNDELY_NATIVE_WIDGET_DEFAULTS: MundelyNativeWidgetSettings = {
  nextStop: {
    bgColor: "#FFFFFF", bgAlpha: 0, iconColor: "#124848", titleColor: "#183B3A", textColor: "#526964",
    accentColor: "#124848", textSize: 13, buttonStyle: "soft", showHeader: true, showTripName: true,
    showSecondary: true, autoUpdate: true, maxChars: 160, maxStops: 0, includePastStops: false
  },
  expenses: {
    bgColor: "#E5EFE9", bgAlpha: 0, iconColor: "#124848", titleColor: "#183B3A", textColor: "#526964",
    accentColor: "#124848", textSize: 13, buttonStyle: "soft", showHeader: true, showTripName: true,
    showSecondary: true, autoUpdate: true
  },
  expenseHistory: {
    bgColor: "#FFFFFF", bgAlpha: 0, iconColor: "#124848", titleColor: "#183B3A", textColor: "#526964",
    accentColor: "#124848", textSize: 13, buttonStyle: "soft", showHeader: true, showTripName: true,
    showSecondary: true, autoUpdate: true
  },
  documents: {
    bgColor: "#FFF1D6", bgAlpha: 0, iconColor: "#805600", titleColor: "#183B3A", textColor: "#526964",
    accentColor: "#124848", textSize: 13, buttonStyle: "soft", showHeader: true, showTripName: true,
    showSecondary: true, autoUpdate: true, documentDisplayMode: "image"
  },
  checklist: {
    bgColor: "#E5EFE9", bgAlpha: 0, iconColor: "#246747", titleColor: "#183B3A", textColor: "#526964",
    accentColor: "#124848", textSize: 13, buttonStyle: "soft", showHeader: true, showTripName: true,
    showSecondary: true, autoUpdate: true, checklistItems: 3
  },
  reminder: {
    bgColor: "#FFF1D6", bgAlpha: 0, iconColor: "#F4C16A", titleColor: "#183B3A", textColor: "#183B3A",
    accentColor: "#124848", textSize: 13, buttonStyle: "soft", showHeader: true, showTripName: true,
    showSecondary: true, autoUpdate: true, maxChars: 500
  },
  calculator: {
    bgColor: "#093232", bgAlpha: 10, iconColor: "#FFFFFF", titleColor: "#FFFFFF", textColor: "#FFFFFF",
    accentColor: "#F4C16A", buttonColor: "#183330", secondaryButtonColor: "#093232", textSize: 13, buttonStyle: "soft",
    showHeader: false, showTripName: false, showSecondary: true, autoUpdate: true, showTitle: false, titleText: "Calcolatrice", showTransaction: true
  },
  converter: {
    bgColor: "#093232", bgAlpha: 10, iconColor: "#FFFFFF", titleColor: "#FFFFFF", textColor: "#FFFFFF",
    accentColor: "#F4C16A", buttonColor: "#183330", secondaryButtonColor: "#093232", textSize: 13, buttonStyle: "soft",
    showHeader: false, showTripName: false, showSecondary: true, autoUpdate: true, showTitle: false, titleText: "Convertitore", showTransaction: true
  }
};

export interface MundelyNativeTripOption {
  id: string;
  name: string;
  dates: string;
}

export interface MundelyNativeItineraryItem {
  id: string;
  title: string;
  secondary: string;
  date: string;
  isPast: boolean;
}

export interface MundelyNativeExpenseItem {
  id: string;
  title: string;
  amount: string;
  convertedAmount?: string;
  date: string;
  payer: string;
  category: string;
  details: string;
}


export interface MundelyNativeRichRun {
  text: string;
  color?: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strike?: boolean;
}

export interface MundelyNativeRichBlock {
  type: "paragraph" | "ordered" | "unordered";
  align?: "left" | "center" | "right" | "justify";
  runs?: MundelyNativeRichRun[];
  items?: Array<{ runs: MundelyNativeRichRun[] }>;
}

export interface MundelyNativeWidgetPayload {
  tripId: string;
  locale?: string;
  primaryCurrency?: string;
  tripName: string;
  tripDates: string;
  tripOptions?: MundelyNativeTripOption[];
  nextStop?: {
    title: string;
    secondary: string;
    projects?: Array<{ tripId: string; tripName: string; title: string; secondary: string; items?: MundelyNativeItineraryItem[] }>;
  } | null;
  expenses: {
    total: string;
    countLabel: string;
    projects?: Array<{ tripId: string; tripName: string }>;
  };
  expenseHistory?: {
    projects: Array<{
      tripId: string;
      tripName: string;
      total: string;
      countLabel: string;
      items: MundelyNativeExpenseItem[];
    }>;
  };
  documents: {
    summary: string;
    secondary: string;
    items?: Array<{ id: string; title: string; secondary: string; kind: "document" | "ticket"; tripId?: string; tripName?: string; url?: string; mimeType?: string }>;
  };
  checklist: {
    summary: string;
    item1?: string;
    item2?: string;
    item3?: string;
    lists?: Array<{
      id: string;
      title: string;
      summary: string;
      tripId?: string;
      tripName?: string;
      items?: Array<{ id: string; title: string; completed: boolean }>;
      item1?: string;
      item2?: string;
      item3?: string;
    }>;
  };
  reminder: {
    title: string;
    secondary: string;
    items?: Array<{ id: string; title: string; secondary: string; html?: string; rich?: MundelyNativeRichBlock[]; tripId?: string; tripName?: string }>;
  };
}

export interface MundelyChecklistWidgetUpdate {
  tripId: string;
  checklistId: string;
  itemId: string;
  completed: boolean;
}

interface MundelyWidgetsPlugin {
  updatePlan(options: { complete: boolean; expires: number }): Promise<void>;
  update(options: { payload: MundelyNativeWidgetPayload }): Promise<void>;
  saveSettings(options: { settings: MundelyNativeWidgetSettings }): Promise<void>;
  saveToolAppearance(options: { kind: "calculator" | "converter"; widgetId: number; settings: MundelyNativeWidgetAppearance }): Promise<void>;
  refresh(): Promise<void>;
  returnHome(): Promise<void>;
  consumeChecklistUpdates(): Promise<{ updates?: MundelyChecklistWidgetUpdate[] }>;
}

const MundelyWidgets = registerPlugin<MundelyWidgetsPlugin>("MundelyWidgets");

const LEGACY_WIDGET_COLORS = {"nextStop":{"bgColor":"#FFF3F9","bgAlpha":0,"iconColor":"#E50086","titleColor":"#17131A","textColor":"#736A77","accentColor":"#E50086","textSize":13,"buttonStyle":"soft","showHeader":true,"showTripName":true,"showSecondary":true,"autoUpdate":true,"maxChars":160,"maxStops":0,"includePastStops":false},"expenses":{"bgColor":"#EAF7FF","bgAlpha":0,"iconColor":"#1677D2","titleColor":"#17131A","textColor":"#736A77","accentColor":"#E50086","textSize":13,"buttonStyle":"soft","showHeader":true,"showTripName":true,"showSecondary":true,"autoUpdate":true},"expenseHistory":{"bgColor":"#F5F7FF","bgAlpha":0,"iconColor":"#7F77DD","titleColor":"#17131A","textColor":"#55505A","accentColor":"#E50086","textSize":13,"buttonStyle":"soft","showHeader":true,"showTripName":true,"showSecondary":true,"autoUpdate":true},"documents":{"bgColor":"#FFF9EB","bgAlpha":0,"iconColor":"#D89000","titleColor":"#17131A","textColor":"#736A77","accentColor":"#E50086","textSize":13,"buttonStyle":"soft","showHeader":true,"showTripName":true,"showSecondary":true,"autoUpdate":true,"documentDisplayMode":"image"},"checklist":{"bgColor":"#ECFAF3","bgAlpha":0,"iconColor":"#149767","titleColor":"#17131A","textColor":"#736A77","accentColor":"#E50086","textSize":13,"buttonStyle":"soft","showHeader":true,"showTripName":true,"showSecondary":true,"autoUpdate":true,"checklistItems":3},"reminder":{"bgColor":"#FFFBEA","bgAlpha":0,"iconColor":"#E79B22","titleColor":"#17131A","textColor":"#17131A","accentColor":"#E50086","textSize":13,"buttonStyle":"soft","showHeader":true,"showTripName":true,"showSecondary":true,"autoUpdate":true,"maxChars":500},"calculator":{"bgColor":"#1E1E30","bgAlpha":10,"iconColor":"#FFFFFF","titleColor":"#FFFFFF","textColor":"#FFFFFF","accentColor":"#315CFF","buttonColor":"#263247","secondaryButtonColor":"#3A4255","textSize":13,"buttonStyle":"soft","showHeader":false,"showTripName":false,"showSecondary":true,"autoUpdate":true,"showTitle":false,"titleText":"Calcolatrice","showTransaction":true},"converter":{"bgColor":"#1E1E30","bgAlpha":10,"iconColor":"#FFFFFF","titleColor":"#FFFFFF","textColor":"#FFFFFF","accentColor":"#315CFF","buttonColor":"#263247","secondaryButtonColor":"#3A4255","textSize":13,"buttonStyle":"soft","showHeader":false,"showTripName":false,"showSecondary":true,"autoUpdate":true,"showTitle":false,"titleText":"Convertitore","showTransaction":true}} as Record<string, Record<string, unknown>>;

function mergeAppearance(kind: MundelyNativeWidgetKind, value?: Partial<MundelyNativeWidgetAppearance>): MundelyNativeWidgetAppearance {
  const base = MUNDELY_NATIVE_WIDGET_DEFAULTS[kind];
  const migrated = { ...value };
  for (const field of ["bgColor", "iconColor", "titleColor", "textColor", "accentColor", "buttonColor", "secondaryButtonColor"] as const) {
    if (typeof migrated[field] === "string" && String(migrated[field]).toLowerCase() === String(LEGACY_WIDGET_COLORS[kind][field]).toLowerCase()) migrated[field] = base[field];
  }
  return {
    ...base,
    ...migrated,
    bgAlpha: Math.max(0, Math.min(100, Number(value?.bgAlpha ?? base.bgAlpha))),
    textSize: Math.max(10, Math.min(28, Number(value?.textSize ?? base.textSize))),
    maxChars: value?.maxChars === undefined && base.maxChars === undefined ? undefined : Math.max(20, Math.min(2000, Number(value?.maxChars ?? base.maxChars ?? 180))),
    checklistItems: value?.checklistItems === undefined && base.checklistItems === undefined ? undefined : Math.max(1, Math.min(20, Number(value?.checklistItems ?? base.checklistItems ?? 3))),
    maxStops: value?.maxStops === undefined && base.maxStops === undefined ? undefined : Math.max(0, Math.min(100, Number(value?.maxStops ?? base.maxStops ?? 0))),
    includePastStops: value?.includePastStops ?? base.includePastStops ?? false,
    documentDisplayMode: value?.documentDisplayMode === "link" ? "link" : (base.documentDisplayMode === "link" ? "link" : "image"),
    showHeader: value?.showHeader ?? base.showHeader,
    showTripName: value?.showTripName ?? base.showTripName,
    showSecondary: value?.showSecondary ?? base.showSecondary,
    autoUpdate: value?.autoUpdate ?? base.autoUpdate,
    buttonStyle: value?.buttonStyle ?? base.buttonStyle
  };
}

export function getMundelyNativeWidgetSettings(): MundelyNativeWidgetSettings {
  try {
    const raw = JSON.parse(localStorage.getItem(MUNDELY_NATIVE_WIDGET_SETTINGS_KEY) || "{}") as Partial<MundelyNativeWidgetSettings>;
    return {
      nextStop: mergeAppearance("nextStop", raw.nextStop),
      expenses: mergeAppearance("expenses", raw.expenses),
      expenseHistory: mergeAppearance("expenseHistory", raw.expenseHistory),
      documents: mergeAppearance("documents", raw.documents),
      checklist: mergeAppearance("checklist", raw.checklist),
      reminder: mergeAppearance("reminder", raw.reminder),
      calculator: mergeAppearance("calculator", raw.calculator),
      converter: mergeAppearance("converter", raw.converter)
    };
  } catch {
    return structuredClone(MUNDELY_NATIVE_WIDGET_DEFAULTS);
  }
}

export async function saveMundelyNativeWidgetSettings(settings: MundelyNativeWidgetSettings): Promise<void> {
  const normalized: MundelyNativeWidgetSettings = {
    nextStop: mergeAppearance("nextStop", settings.nextStop),
    expenses: mergeAppearance("expenses", settings.expenses),
    expenseHistory: mergeAppearance("expenseHistory", settings.expenseHistory),
    documents: mergeAppearance("documents", settings.documents),
    checklist: mergeAppearance("checklist", settings.checklist),
    reminder: mergeAppearance("reminder", settings.reminder),
    calculator: mergeAppearance("calculator", settings.calculator),
    converter: mergeAppearance("converter", settings.converter)
  };
  localStorage.setItem(MUNDELY_NATIVE_WIDGET_SETTINGS_KEY, JSON.stringify(normalized));
  if (Capacitor.getPlatform() !== "android") return;
  try {
    await MundelyWidgets.saveSettings({ settings: normalized });
  } catch {
    // The settings remain available in the web layer and will be retried on the next save.
  }
}

export async function saveMundelyNativeToolWidgetAppearance(kind: "calculator" | "converter", widgetId: number, settings: MundelyNativeWidgetAppearance): Promise<void> {
  if (Capacitor.getPlatform() !== "android" || !Number.isFinite(widgetId) || widgetId <= 0) return;
  try {
    await MundelyWidgets.saveToolAppearance({ kind, widgetId, settings });
  } catch {
    // Global settings remain saved; the specific instance can be retried later.
  }
}

export async function syncMundelyNativeWidgets(payload: MundelyNativeWidgetPayload): Promise<void> {
  if (Capacitor.getPlatform() !== "android") return;
  try {
    await MundelyWidgets.update({ payload });
  } catch {
    // Native widgets are optional outside Android native builds.
  }
}

export async function refreshMundelyNativeWidgets(): Promise<void> {
  if (Capacitor.getPlatform() !== "android") return;
  try {
    await MundelyWidgets.refresh();
  } catch {
    // Best effort only.
  }
}

export async function returnToAndroidHome(): Promise<void> {
  if (Capacitor.getPlatform() !== "android") return;
  try {
    await MundelyWidgets.returnHome();
  } catch {
    // Best effort only.
  }
}

export async function consumeMundelyChecklistWidgetUpdates(): Promise<MundelyChecklistWidgetUpdate[]> {
  if (Capacitor.getPlatform() !== "android") return [];
  try {
    const result = await MundelyWidgets.consumeChecklistUpdates();
    return Array.isArray(result?.updates) ? result.updates : [];
  } catch {
    return [];
  }
}

export async function updateNativeWidgetPlan(complete: boolean, expires: number): Promise<void> {
  if (Capacitor.getPlatform() === "android") await MundelyWidgets.updatePlan({ complete, expires });
}
